/**
 * Mirrors packages/worker/src/aggregates.ts AggregatesResponseBody. Like
 * SubmitResponseBody in types.ts, this is a hand-duplicated mirror, not a
 * shared import (see that file's note) — the wire shape is a Phase 4 draft.
 */
export type PartyBucketKey = "D" | "R" | "I" | "other" | "unknown";

export const PARTY_LABELS: Record<PartyBucketKey, string> = {
  D: "Democrat",
  R: "Republican",
  I: "Independent",
  other: "Other",
  unknown: "Prefer not to say",
};

export interface SupportCounts {
  n: number;
  agree: number;
  disagree: number;
}

/** A cell the server withheld because fewer than `min_cell_n` respondents back
 * it (small-cell suppression, packages/worker/src/aggregates.ts). It has no
 * data fields — render TOO_FEW_RESPONSES, never a number. */
export interface Suppressed {
  suppressed: true;
}

export type Cell<T> = T | Suppressed;

export function isSuppressed(cell: unknown): cell is Suppressed {
  return typeof cell === "object" && cell !== null && (cell as Suppressed).suppressed === true;
}

export const TOO_FEW_RESPONSES = "Too few responses";

export type PolicySupportCounts = Cell<SupportCounts> & {
  by_party: Record<PartyBucketKey, Cell<SupportCounts>>;
};

export interface AggregatesResponseBody {
  /** `{ suppressed: true }` when publishing it would let a reader recover a hidden archetype total. */
  n: Cell<number>;
  updated_at: number;
  /** Unix ms the served daily snapshot was published; null before the first one. Optional: a response cached before this field existed lacks it. */
  published_at?: number | null;
  date_range: { from: number | null; to: number | null };
  quality_threshold: number;
  min_cell_n: number;
  parties: PartyBucketKey[];
  /** Archetypes (and policies) below `min_cell_n` respondents are omitted entirely. */
  by_archetype: Record<string, { all: Cell<number>; by_party: Record<PartyBucketKey, Cell<number>> }>;
  cross_party_agreement: { D: Cell<number>; R: Cell<number> };
  by_axis: {
    axis_a: Record<PartyBucketKey, Cell<number>>;
    axis_b: Record<PartyBucketKey, Cell<number>>;
  };
  by_policy: Record<string, PolicySupportCounts>;
}

export interface FetchAggregatesOptions {
  fetchImpl?: typeof fetch;
}

/** Always a same-origin relative fetch — see main.ts's note on SHARE_BASE
 * for why /api/* never needs an absolute URL from the client. */
export async function fetchAggregates(opts: FetchAggregatesOptions = {}): Promise<AggregatesResponseBody> {
  const doFetch = opts.fetchImpl ?? fetch;
  const response = await doFetch("/api/aggregates");
  if (!response.ok) {
    throw new Error(`Failed to load aggregates (HTTP ${response.status}).`);
  }
  return (await response.json()) as AggregatesResponseBody;
}

/** docs/SCORING.md §4.3 ids, in a fixed display order. */
export const ARCHETYPE_LABELS: Record<string, string> = {
  steadfast_builder: "Steadfast Builder",
  measured_builder: "Measured Builder",
  bold_builder: "Bold Builder",
  practical_builder: "Practical Builder",
  steadfast_localist: "Steadfast Localist",
  measured_localist: "Measured Localist",
  bold_localist: "Bold Localist",
  practical_localist: "Practical Localist",
};

export function archetypeDisplayName(id: string): string {
  return ARCHETYPE_LABELS[id] ?? id.replace(/_/g, " ");
}

export interface CrosstabRow {
  archetype: string;
  label: string;
  total: Cell<number>;
  byParty: Record<PartyBucketKey, Cell<number>>;
}

function suppressedPartyCounts(parties: readonly PartyBucketKey[]): Record<PartyBucketKey, Cell<number>> {
  const out = {} as Record<PartyBucketKey, Cell<number>>;
  for (const p of parties) out[p] = { suppressed: true };
  return out;
}

/**
 * Builds docs/ARCHITECTURE.md §5.3 [LOCKED]'s primary presentation: an
 * archetype × party crosstab with n per cell — never a single headline
 * number. All 8 archetypes are always present so the table's shape doesn't
 * jump around as data arrives; one the server omitted (fewer than
 * `min_cell_n` respondents) is shown fully suppressed. Sorted by total
 * descending, suppressed totals last.
 */
export function buildArchetypeCrosstab(data: AggregatesResponseBody): CrosstabRow[] {
  const rows = Object.keys(ARCHETYPE_LABELS).map((id): CrosstabRow => {
    const counters = data.by_archetype[id];
    return {
      archetype: id,
      label: ARCHETYPE_LABELS[id],
      total: counters?.all ?? { suppressed: true },
      byParty: counters?.by_party ?? suppressedPartyCounts(data.parties),
    };
  });
  const rank = (row: CrosstabRow): number => (isSuppressed(row.total) ? -1 : row.total);
  return rows.sort((a, b) => rank(b) - rank(a));
}

export function formatCount(value: Cell<number>): string {
  return isSuppressed(value) ? TOO_FEW_RESPONSES : String(value);
}

export function formatAxisMean(value: Cell<number> | null): string {
  if (isSuppressed(value)) return TOO_FEW_RESPONSES;
  return value === null ? "—" : value.toFixed(2);
}

export function formatCrossPartyPct(value: Cell<number> | null): string {
  if (isSuppressed(value)) return TOO_FEW_RESPONSES;
  return value === null ? "—" : `${Math.round(value * 100)}%`;
}

const DATE_FORMAT = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });

/** §8.4: "publish n, date range, ... alongside every figure." */
export function formatDateRange(range: { from: number | null; to: number | null } | undefined): string {
  if (typeof range?.from !== "number" || typeof range?.to !== "number") return "no data yet";
  const from = DATE_FORMAT.format(new Date(range.from));
  const to = DATE_FORMAT.format(new Date(range.to));
  return from === to ? from : `${from} – ${to}`;
}

/** Global n: a suppressed n is explained rather than printed as a number. */
export function formatN(value: Cell<number>): string {
  return isSuppressed(value) ? "withheld (small groups)" : String(value);
}

/** "Updated daily; figures as of <date>" (UTC day of the snapshot). */
export function formatPublishedAt(publishedAt: number | null | undefined): string {
  return typeof publishedAt === "number" ? DATE_FORMAT.format(new Date(publishedAt)) : "not yet published";
}
